import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

type UserRole = 'owner' | 'handler' | null;

interface AuthContextType {
  session: Session | null;
  user: User | null;
  role: UserRole;
  isOwner: boolean; // Keep for backward compatibility in components until fully migrated
  isLoading: boolean;
  configMissing: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateEmail: (newEmail: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const CONFIG_MISSING = !SUPABASE_URL || !SUPABASE_ANON_KEY;

// Hard timeout: if auth init takes longer than this, stop spinning and show login
const AUTH_TIMEOUT_MS = 8000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole>(null);
  const [isOwner, setIsOwner] = useState(false);
  // If config is missing we already know we can't auth — skip loading entirely
  const [isLoading, setIsLoading] = useState(!CONFIG_MISSING);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // If env vars are absent, skip all network calls immediately
    if (CONFIG_MISSING) return;

    let mounted = true;

    // Hard-stop the spinner after AUTH_TIMEOUT_MS no matter what
    timeoutRef.current = setTimeout(() => {
      if (mounted) {
        console.warn('Auth initialisation timed out — forcing isLoading=false');
        setIsLoading(false);
      }
    }, AUTH_TIMEOUT_MS);

    async function initializeAuth() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);
          if (session?.user) {
            await checkUserRole(session.user);
          } else {
            setIsLoading(false);
          }
        }
      } catch (error) {
        console.error('Error fetching session:', error);
        if (mounted) setIsLoading(false);
      } finally {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      }
    }

    initializeAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await checkUserRole(session.user);
      } else {
        setRole(null);
        setIsOwner(false);
        setIsLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const checkUserRole = async (user: User) => {
    try {
      // If the user signed in with Google, we treat them as owner
      // Ideally, the DB trigger would set their role, but we check their email domain or provider
      const isGoogleAuth = user.app_metadata?.provider === 'google';
      
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 means no rows found
        console.error('Role check error:', error);
      }

      let currentRole: UserRole = null;
      
      if (data) {
        currentRole = data.role as UserRole;
      } else if (isGoogleAuth) {
        // Fallback for Google auth if role is not yet in DB
        currentRole = 'owner';
        
        // Optionally auto-insert role here or let a database trigger handle it
        await supabase.from('user_roles').upsert([{ user_id: user.id, role: 'owner' }]);
      } else {
        // Default role for email/password users if not set
        currentRole = 'handler';
      }

      setRole(currentRole);
      setIsOwner(currentRole === 'owner');
      console.log('User role:', { userId: user.id, role: currentRole });
    } catch (error) {
      console.error('Error checking user role:', error);
      setRole(null);
      setIsOwner(false);
    } finally {
      setIsLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    
    if (data.user) {
      await checkUserRole(data.user);
    }
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin + '/dashboard',
      }
    });
    if (error) throw error;
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/reset-password',
    });
    if (error) throw error;
  };

  const updateEmail = async (newEmail: string) => {
    const { error } = await supabase.auth.updateUser({ email: newEmail });
    if (error) throw error;
  };

  const signOut = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setRole(null);
      setIsOwner(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ session, user, role, isOwner, isLoading, configMissing: CONFIG_MISSING, signIn, signInWithGoogle, resetPassword, updateEmail, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}